// Sheet de "Generar" — la segunda pantalla del diseño mobile nuevo
// (ADR-2026-07-31-home-mobile-hero-nav-sheet-generar; flujo en dos pasos por
// ADR-2026-07-31-generar-elige-familia-y-recien-ahi-modos; alto por contenido,
// atajo de una sola opción y ediciones rápidas por
// ADR-2026-07-31-sheet-generar-alto-contenido-atajo-ediciones).
//
// Sube desde abajo al tocar el botón central de `NavInferiorMobile`. Es un bottom
// sheet de verdad: ocupa lo que necesita su contenido, con scrim detrás.
//
//   1. Elegir qué generar: imágenes o videos. La tarjeta de videos trae además los
//      atajos a las ediciones rápidas.
//   2. Los modos de esa familia. El título de arriba dice cuál se está mirando y es
//      también el control para cambiar de familia. Si la familia tiene UN solo modo,
//      este paso no existe: se entra derecho a generar.
//
// No cambia la URL a propósito: es una capa sobre la pantalla en la que estabas y
// cerrar te devuelve exactamente ahí. Los modos, las ediciones y qué hace cada cosa
// los decide `InterfazV2` (gating por plan incluido); acá no hay ninguna decisión de
// producto.

import { useEffect, useState, type ComponentType } from 'react';
import { createPortal } from 'react-dom';
import { ModeLockOverlay } from './HomeCards';

export type FamiliaModo = 'imagen' | 'video';

export interface ModoSheet {
  /** `MODES.path` — sólo se usa como key. */
  path: string;
  /** Nombre corto, el mismo que la tira de desktop (`nombreCortoModo`). */
  titulo: string;
  /** Carátula. Estática: en una grilla de seis, seis videos no se sostienen.
   *  Es la apaisada del catálogo: la usa la card destacada, que va en 3:2. */
  imagen: string;
  /**
   * La versión 1:1 de la carátula, si el modo tiene una
   * (ADR-2026-08-04-la-portada-cuadrada-no-es-de-mobile). La usan las cards de la
   * grilla, que son cuadradas. Sin ella se cae a `imagen`, recortada.
   */
  imagenCuadrada?: string;
  familia: FamiliaModo;
  /**
   * El que abre la lista EN GRANDE (3:2), arriba de la grilla —
   * ADR-2026-08-20-el-modo-en-grande-de-generar-sigue-al-rubro.
   *
   * Lo marca el llamador (`useModosGenerar`, vía `modoDestacadoGenerar`) y no lo decide
   * este archivo: cuál va en grande depende del RUBRO de la marca activa, que es algo
   * que el sheet no sabe ni tiene por qué averiguar. Antes era un `path` fijo acá
   * adentro, y para Hogar y Decoración apuntaba a un modo que ese rubro no ve.
   *
   * Sólo lo mira la familia de imágenes: en videos todas las cards van en grande
   * (ADR-2026-08-05-el-sheet-de-video-va-en-una-columna).
   */
  destacado?: boolean;
  /** El plan del usuario no lo incluye: se dibuja el candado. */
  bloqueado: boolean;
  /** Qué hacer al tocarlo. Lo resuelve el llamador, con su gating. */
  onAbrir: () => void;
}

/** Un atajo de "Ediciones rápidas", el mismo catálogo que la home. */
export interface EdicionSheet {
  label: string;
  /** Ícono de lucide, como en la home. */
  Icono: ComponentType<{ className?: string }>;
  /**
   * El plan no incluye el modo al que lleva este atajo —
   * ADR-2026-08-20-los-atajos-de-carrusel-y-animar-van-a-su-modo. Hoy sólo Animar.
   * `onAbrir` ya resuelve el destino (planes en vez del modo); esto es para DIBUJARLO,
   * así el atajo se ve bloqueado antes de tocarlo, igual que las cards de modo de acá
   * arriba.
   */
  bloqueado?: boolean;
  onAbrir: () => void;
}

/** Cómo se llama cada familia en el paso 1 (la pregunta es "qué generar"). */
const ETIQUETA_FAMILIA: Record<FamiliaModo, string> = {
  imagen: 'Imágenes',
  video: 'Videos',
};

/** Cómo se llama en el paso 2 (el título es dónde estás parado, no una opción). */
const TITULO_FAMILIA: Record<FamiliaModo, string> = {
  imagen: 'Creación de imagen',
  video: 'Creación de video',
};

const FAMILIAS: FamiliaModo[] = ['imagen', 'video'];

/**
 * Portada propia de una familia — ADR-2026-08-04-imagenes-tiene-su-propia-portada.
 *
 * Por defecto la card usa la carátula del primer modo de la familia (ver
 * `caratulaFamilia`), que es lo que evita mantener assets: sirve mientras la card sea
 * "una muestra de lo que hay adentro". Pero la pregunta del paso 1 es de CATEGORÍA
 * —imágenes o videos—, y una sola foto de producto la responde a medias: la de
 * Imágenes mostraba la remera del modo Automático, o sea un modo, no la familia.
 *
 * Acá va la portada que representa a la familia entera. Las que no estén listadas
 * siguen con el comportamiento viejo, y si el archivo falta la card cae a esa misma
 * carátula (ver el `onError` de la imagen): el asset puede llegar después sin dejar un
 * hueco mientras tanto.
 */
const PORTADA_FAMILIA: Partial<Record<FamiliaModo, string>> = {
  imagen: '/images/generar/portada_imagenes.jpg',
  video: '/images/generar/portada_videos.png',
};

/* El modo que abre la lista en grande (ADR-2026-08-04-el-sheet-de-generar-destaca-un-modo)
   ya NO se decide acá: llega marcado con `destacado` desde `useModosGenerar` —
   ADR-2026-08-20-el-modo-en-grande-de-generar-sigue-al-rubro.

   Era `const MODO_DESTACADO = 'modelo'`, un `path` fijo. La idea de usar un path y no un
   índice sigue valiendo —si el modo se cae por rubro, por plan o por feature flag no hay
   destacada y todos van a la grilla, y la pantalla sigue en pie—, pero el path correcto
   depende del RUBRO de la marca activa, que este archivo no conoce: para Hogar y
   Decoración `'modelo'` apuntaba a un modo que ese rubro ni siquiera ve, así que la
   pantalla se quedaba sin destacada. */

/**
 * Una card de modo: la portada del carrusel del inicio, el título encima y el candado
 * del plan si corresponde.
 *
 * `destacada` cambia el alto y el peso del título, no la estructura: es la misma card
 * en dos tamaños, así el estado bloqueado y el degradé no se escriben dos veces.
 *
 * El degradé va de abajo hacia arriba en las dos —y no de izquierda a derecha como en
 * las bandas de 80px— porque el título ahora se apoya en el borde inferior, que es
 * donde tiene que haber contraste.
 */
function CardModo({
  modo,
  onClick,
  destacada = false,
}: {
  modo: ModoSheet;
  onClick: () => void;
  destacada?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      // 3:2 la destacada y 1:1 las de la grilla —
      // ADR-2026-08-04-las-cards-del-sheet-de-generar-toman-la-forma-de-su-portada.
      // Las dos proporciones son las de los archivos que muestran, así que la carátula
      // entra entera en vez de recortada: la destacada era `h-40` (2.24 con el ancho
      // del sheet) y la grilla `aspect-[4/3]`.
      className={`relative flex w-full items-end overflow-hidden rounded-[12px] active:opacity-90 ${
        destacada ? 'aspect-[3/2]' : 'aspect-square'
      }`}
    >
      <img
        // La destacada va en 3:2, que es la forma de la carátula del catálogo; las de
        // la grilla son cuadradas y usan la portada 1:1 del modo si tiene una.
        src={destacada ? modo.imagen : (modo.imagenCuadrada ?? modo.imagen)}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <span
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"
      />
      {/* El candado va sobre la carátula, igual que en la home y en `/modos`: el modo
          se ve —para que se pueda descubrir— pero se avisa que el plan no lo incluye. */}
      {modo.bloqueado && <ModeLockOverlay />}
      <span
        className={`relative z-[3] w-full px-4 pb-3 text-left font-semibold leading-tight text-white ${
          destacada ? 'text-[17px]' : 'text-[14px]'
        }`}
      >
        {modo.titulo}
      </span>
    </button>
  );
}

export function SheetGenerar({
  modos,
  ediciones,
  titulo = '¿Qué te gustaría generar?',
  onClose,
}: {
  modos: ModoSheet[];
  ediciones: EdicionSheet[];
  /**
   * Título del paso 1 — ADR-2026-08-05-el-sheet-de-generar-nombra-el-sujeto.
   *
   * Por defecto es la pregunta abierta de la home, donde no hay nada elegido todavía.
   * Abierto desde Mi marca ya hay un producto o un modelo sobre la mesa, y el llamador
   * lo nombra acá: la pregunta genérica no decía que lo que se elija se aplica a eso.
   */
  titulo?: string;
  onClose: () => void;
}) {
  // Entrada animada: se monta abajo y sube en el tick siguiente. Con el estilo
  // final desde el primer paint no habría transición que correr (mismo patrón que
  // `OptionSheet`).
  const [animado, setAnimado] = useState(false);
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setAnimado(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  // `null` = paso 1 (elegir familia). Con una familia elegida, paso 2.
  const [familia, setFamilia] = useState<FamiliaModo | null>(null);
  // Menú del título abierto/cerrado (sólo existe en el paso 2).
  const [menuAbierto, setMenuAbierto] = useState(false);
  // Hacia dónde fue el último movimiento, para que la transición entre pasos
  // acompañe: entrando, el contenido llega desde la derecha; volviendo, desde la
  // izquierda. Son las animaciones que ya usa el cambio de categoría en plantillas.
  const [avanzando, setAvanzando] = useState(true);

  // Escape: primero cierra el menú, después vuelve al paso 1, y recién ahí el
  // sheet. Que la tecla deshaga UNA capa por vez es lo que se espera de una pila.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (menuAbierto) setMenuAbierto(false);
      else if (familia) volverAFamilias();
      else onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuAbierto, familia, onClose]);

  const modosDe = (f: FamiliaModo) => modos.filter((m) => m.familia === f);

  // Las familias que EXISTEN. Con "Video con influencer" apagado por feature flag
  // no hay ningún modo de video, y la card prometería algo que no se puede generar.
  const familiasDisponibles = FAMILIAS.filter((f) => modosDe(f).length > 0);

  /** El respaldo de siempre: la carátula del primer modo de la familia. */
  const caratulaDelPrimerModo = (f: FamiliaModo) => modosDe(f)[0]?.imagen;
  /** La portada de la familia, con ese respaldo si no tiene una propia. */
  const caratulaFamilia = (f: FamiliaModo) =>
    PORTADA_FAMILIA[f] ?? caratulaDelPrimerModo(f);

  const abrirModo = (m: ModoSheet) => {
    // Cerrar SIEMPRE, también con el modo bloqueado: `onAbrir` levanta ahí el
    // visor de ejemplos o el modal de plan, y quedarían debajo de este sheet.
    m.onAbrir();
    onClose();
  };

  /**
   * Elegir una familia. Con UNA sola opción adentro se entra derecho a generar:
   * el paso 2 sería una pantalla con una card, que es hacer elegir entre una cosa.
   * Hoy es el caso de Videos, con "Video con influencer" como único modo; si mañana
   * hay dos, la lista vuelve sola.
   */
  const elegirFamilia = (f: FamiliaModo) => {
    const suyos = modosDe(f);
    if (suyos.length === 1) {
      abrirModo(suyos[0]);
      return;
    }
    setAvanzando(true);
    setFamilia(f);
  };

  /** Volver al paso 1, con la transición en sentido inverso. */
  const volverAFamilias = () => {
    setMenuAbierto(false);
    setAvanzando(false);
    setFamilia(null);
  };

  return createPortal(
    <>
      {/* Scrim: oscurece lo de atrás y cierra al tocarlo. Con el sheet a media
          pantalla, la home sigue a la vista y sin él no se lee cuál de las dos
          capas está activa. */}
      <div
        aria-hidden="true"
        onClick={onClose}
        className={`fixed inset-0 z-[69] bg-black/40 transition-opacity duration-300 sm:hidden ${
          animado ? 'opacity-100' : 'opacity-0'
        }`}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={familia ? TITULO_FAMILIA[familia] : titulo}
        // Alto POR CONTENIDO (ADR-2026-07-31-sheet-generar-alto-contenido-atajo-ediciones): anclado abajo, crece con lo que
        // tiene adentro y recién ahí empieza a scrollear. Antes era `inset-0`, o sea
        // siempre la pantalla entera, con la mitad vacía en el paso 1.
        //
        // El techo es la pantalla menos el safe area y 8px de aire —
        // ADR-2026-08-04-el-sheet-de-generar-crece-hasta-arriba—, el mismo que usa
        // `SheetLateral`. Era `88vh`, que dejaba 100px muertos arriba con la lista ya
        // cortada abajo: con la card de Plantillas la grilla no entra, y esos 100px
        // son media card. `dvh` y no `vh` porque en el teléfono la barra del navegador
        // aparece y desaparece, y `vh` mide siempre el caso grande.
        className={`fixed inset-x-0 bottom-0 z-[70] max-h-[calc(100dvh-env(safe-area-inset-top,0px)-0.5rem)] overflow-y-auto overscroll-contain rounded-t-[24px] bg-white shadow-[0_-8px_40px_rgba(2,6,43,0.18)] transition-transform duration-300 ease-out sm:hidden ${
          animado ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)' }}
      >
        {/* Manija: no arrastra (todavía), pero es lo que dice "esto es un sheet y
            se puede cerrar" antes de buscar la X. */}
        <div aria-hidden="true" className="flex justify-center pb-1 pt-2.5">
          <span className="h-1 w-9 rounded-full bg-black/15" />
        </div>

        {/* Transición entre pasos (ADR-2026-07-31-sheet-generar-pasos-transicion-listado-vertical): el `key` fuerza el remonte
            para que la animación vuelva a correr, y la dirección acompaña al
            movimiento. Son las mismas clases que usa el cambio de categoría en
            plantillas, así los dos swaps de contenido se sienten igual. */}
        <div
          key={familia ?? 'familias'}
          className={avanzando ? 'animate-category-swap' : 'animate-category-swap-back'}
        >
        {familia === null ? (
          /* ── Paso 1: qué generar ───────────────────────────────────── */
          <div className="flex flex-col gap-5 px-5 pb-1 pt-4">
            <div className="flex items-center justify-between gap-3">
              <h1 className="text-[20px] font-bold leading-[26px] text-[#18181B]">
                {titulo}
              </h1>
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar"
                className="-mr-1 grid h-8 w-8 shrink-0 place-items-center rounded-full text-brand-black/70 active:opacity-60"
              >
                <span className="material-symbols-rounded text-[24px]">close</span>
              </button>
            </div>

            <div className="flex flex-col gap-4">
              {familiasDisponibles.map((f) => {
                const caratula = caratulaFamilia(f);
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => elegirFamilia(f)}
                    className="relative flex h-[176px] w-full items-center justify-center overflow-hidden rounded-[20px] ring-1 ring-black/[0.06] active:opacity-90"
                  >
                    {caratula && (
                      <img
                        src={caratula}
                        alt=""
                        aria-hidden="true"
                        // Si la portada propia todavía no está en `public/`, la card
                        // cae a la carátula del primer modo en vez de quedar en un
                        // rectángulo vacío. El `dataset` marca que ya se reintentó:
                        // sin eso, un respaldo que también falle deja el `onError`
                        // disparándose en loop.
                        onError={(e) => {
                          const img = e.currentTarget;
                          if (img.dataset.respaldo === '1') return;
                          const respaldo = caratulaDelPrimerModo(f);
                          if (!respaldo) return;
                          img.dataset.respaldo = '1';
                          img.src = respaldo;
                        }}
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    )}
                    {/* Degradé para que el texto se lea sobre cualquier carátula.
                        El tramo del MEDIO es el que importa: el título va centrado,
                        y antes ahí el velo era `black/10` —pensado para no apagar el
                        collage claro de Imágenes— con el resultado de que las dos
                        etiquetas quedaban al borde de lo legible. Ahora el centro
                        tiñe de verdad y el degradé sigue abriendo hacia abajo, así
                        la carátula no queda plana bajo un velo parejo. */}
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/40 to-black/70"
                    />
                    {/* La sombra del texto se mantiene además del velo: sobre el
                        collage claro de Imágenes es lo que despega las letras de los
                        recortes blancos que quedan justo detrás. */}
                    <span className="relative text-[20px] font-semibold leading-[25px] text-white [text-shadow:0_1px_14px_rgba(0,0,0,0.5)]">
                      {ETIQUETA_FAMILIA[f]}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* ── Ediciones rápidas ─────────────────────────────────────
                Debajo de las dos tarjetas y no colgando de una de ellas
                (ADR-2026-07-31-ediciones-rapidas-fila-iconos-debajo, supersede el punto 3 de
                ADR-2026-07-31-sheet-generar-alto-contenido-atajo-ediciones): son acciones sobre algo YA
                generado, así que no pertenecen ni a imágenes ni a videos — son la
                tercera cosa que se puede hacer desde acá.

                Íconos en círculo con la etiqueta debajo, como la referencia: a este
                tamaño entran las siete en dos filas sin scroll, y cada una se lee
                de un vistazo. Los chips en fila que había antes escondían la mitad
                detrás de un scroll horizontal. */}
            {ediciones.length > 0 && (
              <div className="flex flex-col gap-3">
                <h2 className="text-[15px] font-semibold text-[#18181B]">
                  Ediciones rápidas
                </h2>
                <div className="grid grid-cols-5 gap-x-2 gap-y-3">
                  {ediciones.map(({ label, Icono, bloqueado, onAbrir }) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => {
                        onAbrir();
                        onClose();
                      }}
                      className="flex flex-col items-center gap-1 active:opacity-60"
                    >
                      {/* Círculo gris claro + ícono, el mismo par que usan las
                          tarjetas de "Ediciones rápidas" de la home (ahí es un
                          cuadrado redondeado porque la tarjeta es grande; acá el
                          ícono va solo, y el círculo es lo que lo sostiene).

                          Bloqueado: candado en lugar del ícono, igual que la home y que
                          la sidebar (ADR-2026-08-20-los-atajos-de-carrusel-y-animar-van-a-su-modo).
                          Sin esto el atajo se veía idéntico al resto y el tap expulsaba
                          a la pantalla de planes sin ningún aviso previo. */}
                      <span className="grid h-11 w-11 place-items-center rounded-full bg-[#F4F4F5] text-[#16181D]/75">
                        {bloqueado ? (
                          <span className="material-symbols-rounded text-[19px]">
                            lock
                          </span>
                        ) : (
                          <Icono className="h-[17px] w-[17px]" />
                        )}
                      </span>
                      {/* Dos líneas como techo: "Descripción" entra en una, pero
                          una etiqueta más larga no debe empujar la fila de al lado. */}
                      <span className="line-clamp-2 text-center text-[10.5px] font-medium leading-tight text-black/60">
                        {label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* ── Paso 2: los modos de la familia elegida ───────────────── */
          <div className="flex flex-col gap-4 pb-1">
            {/* Barra de arriba: volver a la izquierda, cerrar a la derecha y el
                título CENTRADO en el medio. Las dos acciones miden lo mismo (36px)
                para que el título quede centrado respecto de la pantalla y no
                entre ellas. `relative` porque el menú cuelga de acá.

                Se PEGA arriba (ADR-2026-08-04-el-sheet-de-generar-crece-hasta-arriba):
                el sheet entero es el scroller, así que sin esto la lista se lleva el
                título y —peor— el volver y el cerrar, justo cuando el sheet ocupa toda
                la pantalla y no queda nada afuera para tocar. Necesita fondo propio:
                las cards pasan por debajo. */}
            <div className="sticky top-0 z-20 flex h-9 items-center justify-between bg-white px-4 pt-1">
              <button
                type="button"
                onClick={volverAFamilias}
                aria-label="Volver"
                className="grid h-9 w-9 place-items-center rounded-full text-brand-black/70 active:opacity-60"
              >
                <span className="material-symbols-rounded text-[24px]">arrow_back</span>
              </button>

              {/* El título ES el control para cambiar de familia: dice dónde estás
                  parado y, con el chevron, que hay otras. Un selector aparte
                  repetiría en la pantalla lo que el título ya dice. */}
              <button
                type="button"
                onClick={() => setMenuAbierto((v) => !v)}
                aria-expanded={menuAbierto}
                aria-haspopup="menu"
                className="flex items-center gap-1 active:opacity-60"
              >
                <span className="text-[17px] font-bold text-[#18181B]">
                  {TITULO_FAMILIA[familia]}
                </span>
                <span
                  className={`material-symbols-rounded text-[20px] text-brand-black/60 transition-transform ${
                    menuAbierto ? 'rotate-180' : ''
                  }`}
                >
                  expand_more
                </span>
              </button>

              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar"
                className="grid h-9 w-9 place-items-center rounded-full text-brand-black/70 active:opacity-60"
              >
                <span className="material-symbols-rounded text-[24px]">close</span>
              </button>

              {/* Menú del título. Lista las familias con la actual marcada: se ve
                  lo elegido y se cambia desde el mismo lugar.

                  El centrado lo hace este contenedor con `flex justify-center` y
                  NO un `-translate-x-1/2` en el panel: el panel entra con
                  `animate-fade-in`, cuyos keyframes fijan `transform`, y esa
                  animación pisaba el translate — el menú aparecía corrido media
                  pantalla a la derecha (ADR-2026-07-31-sheet-generar-alto-contenido-atajo-ediciones). */}
              {menuAbierto && (
                <>
                  {/* Capa para cerrar tocando afuera. Va antes que el panel y con
                      un z menor, así el tap de adentro no la toca. */}
                  <button
                    type="button"
                    aria-hidden="true"
                    tabIndex={-1}
                    onClick={() => setMenuAbierto(false)}
                    className="fixed inset-0 z-[1] cursor-default"
                  />
                  <div className="absolute inset-x-0 top-full z-[2] flex justify-center pt-2">
                    <div
                      role="menu"
                      className="animate-fade-in w-[232px] overflow-hidden rounded-2xl bg-white shadow-[0_8px_28px_rgba(2,6,43,0.16)] ring-1 ring-black/[0.06]"
                    >
                      {familiasDisponibles.map((f) => {
                        const actual = f === familia;
                        return (
                          <button
                            key={f}
                            role="menuitemradio"
                            aria-checked={actual}
                            type="button"
                            onClick={() => {
                              setMenuAbierto(false);
                              // Por el mismo criterio del paso 1: si la familia
                              // tiene un solo modo, no hay lista que mostrar.
                              elegirFamilia(f);
                            }}
                            className="flex w-full items-center justify-between px-4 py-3 text-left active:bg-black/[0.04]"
                          >
                            <span
                              className={`text-[15px] ${
                                actual ? 'font-semibold text-brand-primary' : 'text-[#18181B]'
                              }`}
                            >
                              {TITULO_FAMILIA[f]}
                            </span>
                            {actual && (
                              <span className="material-symbols-rounded text-[20px] text-brand-primary">
                                check
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Una destacada arriba y el resto de a dos —
                ADR-2026-08-04-el-sheet-de-generar-destaca-un-modo. Antes eran cinco
                bandas iguales de 80px (ADR-2026-07-31-sheet-generar-pasos-transicion-listado-vertical),
                una lista sin jerarquía donde el modo más usado no se distinguía y las
                portadas se veían recortadas a una franja. La destacada tiene alto para
                que su portada se lea; las otras cuatro entran en dos filas, así la
                grilla completa se ve sin scrollear. */}
            {(() => {
              const modos = modosDe(familia);

              // Video va en una sola columna, todas las cards al ancho del sheet y en 3:2
              // (ADR-2026-08-05-el-sheet-de-video-va-en-una-columna): son dos o tres modos,
              // así que la grilla de a dos no ahorraba scroll y sí achicaba la portada —
              // que acá es un frame de video y a 1:1 se recortaba a un cuadrado del centro.
              if (familia === 'video') {
                return (
                  <div className="flex flex-col gap-2.5 px-5">
                    {modos.map((m) => (
                      <CardModo key={m.path} modo={m} onClick={() => abrirModo(m)} destacada />
                    ))}
                  </div>
                );
              }

              const destacada = modos.find((m) => m.destacado);
              const resto = destacada ? modos.filter((m) => m !== destacada) : modos;
              return (
                <div className="flex flex-col gap-2.5 px-5">
                  {destacada && (
                    <CardModo modo={destacada} onClick={() => abrirModo(destacada)} destacada />
                  )}
                  {resto.length > 0 && (
                    <div className="grid grid-cols-2 gap-2.5">
                      {resto.map((m) => (
                        <CardModo key={m.path} modo={m} onClick={() => abrirModo(m)} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
        </div>
      </div>
    </>,
    document.body,
  );
}
